import { Dispatch, FormEvent, ForwardedRef, SetStateAction, useContext, useEffect, useRef, useState } from "react";

import { FileUploadHandle, LayeredSelectData, SelectData, UploadedDocument } from "../../../../../../../models";
import { uploadProjectDocument } from "../../../../../../../service/project";
import {
  getProjectDetails,
  GetProjectDetailsResponse,
  getProjectDocumentVersion,
  GetProjectDocumentVersionResponse,
  searchProjectDocumentHistory,
  searchProjectDocuments,
  searchProjectDocumentTypes,
  SearchProjectDocumentTypesResponse,
  searchProjectOrganisations,
} from "../../../../../../../service/query";
import { searchProjectDocumentTypeVariants } from "../../../../../../../service/query/QueryService.full";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../../../../useAuth";
import { distinctBy, useIsLoadingWrapper } from "../../../../../../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../../../../../../widget";
import { ProjectContext } from "../../../ProjectContext";

interface UseUploadDocumentModalProps {
  closeModal: () => void;
  refreshDocuments?: boolean;
  setRefreshDocuments: Dispatch<SetStateAction<boolean>>;
  uploadDocumentUuid?: string;
  uploadDocumentLatestVersionUuid?: string;
  isNonStandard?: boolean;
  groupUuid?: string;
  projectUuid?: string | null;
  /*
    Reports the documents an upload produced, for callers that have to act on them (the task overlay links
    them to a task). The upload endpoint returns no uuid, so they are resolved by re-reading the project's
    documents — see resolveUploadedDocuments.
  */
  onUploaded?: (documents: UploadedDocument[]) => void;
}

interface UseUploadDocumentModalReturnData {
  fileUploadRef: ForwardedRef<FileUploadHandle>;
  projectDetails: GetProjectDetailsResponse | undefined;
  latestDocumentDetails: GetProjectDocumentVersionResponse | undefined;
  visibility: boolean | undefined;
  isGroup: boolean | undefined;
  maxFileSize?: number;
  supportsMultipleFiles: boolean;
  documentTypes?: LayeredSelectData;
  documentVariants?: LayeredSelectData;
  selectedDocumentVariant?: string;
  relatedOrganisations?: SelectData;
  isHandleSubmitLoading: boolean;
  handleVisibilityChange: (v: boolean) => void;
  setIsGroup: Dispatch<SetStateAction<boolean | undefined>>;
  setFiles: Dispatch<SetStateAction<File[] | undefined>>;
  setSelectedOrganisations: Dispatch<SetStateAction<string[] | undefined>>;
  handleDocumentTypeChange: (typeUuid: string) => Promise<void>;
  setSelectedDocumentVariant: Dispatch<SetStateAction<string | undefined>>;
  setCustomVariant: Dispatch<SetStateAction<string | undefined>>;
  handleCancel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  showExistsWarning: boolean;
  showExternalRegistryWarning: boolean;
  errors: ServiceError[];
}

export const useUploadDocumentModal = ({
  closeModal,
  refreshDocuments,
  setRefreshDocuments,
  uploadDocumentUuid,
  uploadDocumentLatestVersionUuid,
  isNonStandard,
  groupUuid,
  projectUuid,
  onUploaded,
}: UseUploadDocumentModalProps): UseUploadDocumentModalReturnData => {
  const fileUploadRef = useRef<FileUploadHandle>(null);
  const { currentOrganisationUuid } = useAuth();
  const { groupUuid: groupUuidInContext, projectDetails: projectDetailsInContext } = useContext(ProjectContext);
  /*
    The project the upload is scoped to. ProjectContext supplies it on a project page, but the modal is also
    opened from hosts that have no ProjectProvider — the task overlay reaches it from the dashboards, the
    tasks board and the activities dashboard — and the document type list, the group flag and the upload
    payload all need the project itself. Read here for those hosts rather than left undefined, which used to
    mean the overlay had to hide the whole Attachments section on them.
  */
  const [fetchedProjectDetails, setFetchedProjectDetails] = useState<GetProjectDetailsResponse>();
  const projectDetails = projectDetailsInContext ?? fetchedProjectDetails;
  const resolvedProjectUuid = projectUuid || projectDetails?.uuid || undefined;
  const resolvedGroupUuid = groupUuid || projectDetails?.group?.groupUuid || groupUuidInContext || undefined;
  /*
    The project the read below has finished for — set once it settles, whether or not it resolved, so a
    project that fails to load falls through to the general type list rather than leaving the modal none.
    Keyed on the uuid so switching project closes the gate again in the same render the uuid changes.
  */
  const [readProjectUuid, setReadProjectUuid] = useState<string>();
  /*
    True exactly while that read is outstanding. The document type list is filtered by the project's
    standard, so searching it before the project lands would fetch a general-only list and then a second,
    correct one — two responses that can land in either order, the general-only one overwriting the types
    the user actually needs. Nothing to wait for where the context supplies the project, or where there is
    no project at all (a group-only upload passes groupUuid and no project).
  */
  const isAwaitingProject =
    projectDetailsInContext === undefined &&
    resolvedProjectUuid !== undefined &&
    readProjectUuid !== resolvedProjectUuid;
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [latestDocumentDetails, setLatestDocumentDetails] = useState<GetProjectDocumentVersionResponse>();
  const [searchDocumentTypesResponse, setSearchDocumentTypesResponse] = useState<SearchProjectDocumentTypesResponse>();
  const [documentTypes, setDocumentTypes] = useState<LayeredSelectData>();
  const [documentVariants, setDocumentVariants] = useState<LayeredSelectData>();
  const [relatedOrganisations, setRelatedOrganisations] = useState<SelectData>();
  const [maxFileSize, setMaxFileSize] = useState<number>();

  // NOTE - we could refactor this to be a single state variable as we have done in other places (eg. src\templates\WorkflowTaskPanel\useWorkflowTaskPanel.ts)
  const [selectedOrganisations, setSelectedOrganisations] = useState<string[]>();
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>();
  const [selectedDocumentVariant, setSelectedDocumentVariant] = useState<string>();
  const [customVariant, setCustomVariant] = useState<string>();
  const [visibility, setVisibility] = useState<boolean>();
  const [supportsMultipleFiles, setSupportsMultipleFiles] = useState<boolean>(false);
  const [supportedMimeTypes, setSupportedMimeTypes] = useState<string[]>();
  const [isGroup, setIsGroup] = useState<boolean>();
  const [files, setFiles] = useState<File[]>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [showExistsWarning, setShowExistsWarning] = useState(false);
  const [showExternalRegistryWarning, setShowExternalRegistryWarning] = useState(false);

  const handleCancel = (): void => {
    if (fileUploadRef.current) fileUploadRef.current.clearInput();
    setDocumentTypes(undefined);
    setDocumentVariants(undefined);
    setSelectedOrganisations(undefined);
    setSelectedDocumentType(undefined);
    setSelectedDocumentVariant(undefined);
    setCustomVariant(undefined);
    setVisibility(undefined);
    setIsGroup(undefined);
    setFiles(undefined);
    setSupportsMultipleFiles(false);
    setShowExistsWarning(false);
    setShowExternalRegistryWarning(false);

    setErrors([]);
    closeModal();
  };

  const handleVisibilityChange = (v: boolean): void => {
    setVisibility(v);
    setSelectedOrganisations(undefined);
  };

  const handleDocumentTypeChange = async (type: string): Promise<void> => {
    const typeDetails = searchDocumentTypesResponse?.results.find((d) => d.name === type);
    setMaxFileSize(typeDetails?.maxFileSize != null ? typeDetails.maxFileSize * 0.000001 : 1);
    setSelectedDocumentType(typeDetails?.uuid);
    setSupportedMimeTypes(typeDetails?.supportedMimeTypes);
    setSupportsMultipleFiles(typeDetails?.supportsMultipleFiles || false);
    setDocumentVariants(undefined);
    setSelectedDocumentVariant(undefined);

    if (typeDetails?.supportsVariants) {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.results = {
        typeUuid: {
          operator: "eq",
          value: typeDetails.uuid,
        },
      };

      await searchProjectDocumentTypeVariants({
        paging: { beforeCursor: null, afterCursor: null, limit: 50 },
        filter: filterCriteria,
      }).then((response) => {
        if (!typeDetails?.supportsMultipleFiles) {
          setDocumentVariants(
            response.data?.results && response.data?.results.length > 0
              ? [
                  {
                    optionsHeader: typeDetails.name || "",
                    options: response.data?.results.map((v) => ({ key: v.uuid, value: v.name })) as SelectData,
                  },
                  {
                    optionsHeader: { key: "Custom", value: "Custom" },
                    options: [],
                  },
                  {
                    optionsHeader: { key: "N/A", value: "N/A" },
                    options: [],
                  },
                ]
              : [
                  {
                    optionsHeader: { key: "Custom", value: "Custom" },
                    options: [],
                  },
                  {
                    optionsHeader: { key: "N/A", value: "N/A" },
                    options: [],
                  },
                ]
          );
        }
      });
    } else {
      setDocumentVariants(undefined);
    }
  };

  const getDocumentDetails = async (): Promise<void> => {
    if (uploadDocumentLatestVersionUuid) {
      await getProjectDocumentVersion({ projectDocumentHistoryUuid: uploadDocumentLatestVersionUuid }).then(
        (response) => {
          if (response.data?.documentType.maxFileSize != null)
            // eslint-disable-next-line no-unsafe-optional-chaining
            setMaxFileSize(response.data?.documentType.maxFileSize * 0.000001);
          setIsGroup(response.data?.groupId !== null);
          setLatestDocumentDetails(response.data);
          setSupportedMimeTypes(response.data?.documentType.supportedMimeTypes);
        }
      );
    } else {
      setLatestDocumentDetails(undefined);
      if (groupUuid) {
        setIsGroup(true);
      } else {
        setIsGroup(projectDetails?.group !== null ? undefined : false);
      }

      const filterCriteria = dataGridMapFilterCriteria([]);
      if (isNonStandard) {
        filterCriteria.results = {
          standard: {
            standardUuid: {
              operator: "eq",
              value: null,
            },
          },
        };
      } else {
        filterCriteria.results = {
          standard: {
            standardUuid: {
              operator: projectDetails !== undefined && projectDetails?.standard !== undefined ? "in" : "eq",
              value:
                projectDetails !== undefined && projectDetails?.standard !== undefined
                  ? `${projectDetails?.standard.uuid}, null`
                  : null,
            },
          },
          isGenerated: {
            operator: "eq",
            value: false,
          },
        };
      }

      await searchProjectDocumentTypes({
        paging: { beforeCursor: null, afterCursor: null, limit: 50 },
        filter: filterCriteria,
      }).then((response) => {
        const generalTypes = response.data?.results
          .filter((r) => r.standard === null)
          .map((r) => ({ key: r.uuid, value: r.name }));
        const standardTypes = projectDetails?.standard.uuid
          ? response.data?.results
              .filter((r) => r.standard?.standardUuid === projectDetails?.standard.uuid)
              .map((r) => ({ key: r.uuid, value: r.name }))
          : [];
        const docTypes = [
          ...(standardTypes && standardTypes?.length > 0
            ? [
                {
                  optionsHeader: projectDetails?.standard.displayName || "",
                  options: standardTypes as SelectData,
                },
              ]
            : []),
          ...(generalTypes && generalTypes?.length > 0
            ? [
                {
                  optionsHeader: "General",
                  options: generalTypes as SelectData,
                },
              ]
            : []),
        ];
        setDocumentTypes(docTypes);
        setSearchDocumentTypesResponse(response.data);
      });
    }
    if (resolvedProjectUuid || resolvedGroupUuid) {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.results = {
        role: {
          operator: "in",
          value: "Developer, Development Manager, Guest - Write, Guest - Read, Asset Manager",
        },
      };

      await searchProjectOrganisations({
        projectUuid: resolvedProjectUuid,
        groupUuid: resolvedGroupUuid,
        paging: { beforeCursor: null, afterCursor: null, limit: 50 },
        filter: filterCriteria,
      }).then((response) => {
        const guestOrganistions = distinctBy(
          response.data?.results
            .filter((f) => f.organisationUuid !== currentOrganisationUuid)
            .map((r) => ({ key: r.organisationUuid, value: r.displayName })) ?? [],
          (item) => item.key
        );

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        // We need to ensure that the list of organisations is unique as it's possible for an organisation to have multiple roles
        setRelatedOrganisations(guestOrganistions as SelectData);
      });
    }
  };

  const checkExists = async (): Promise<boolean> => {
    const type = searchDocumentTypesResponse?.results.find((r) => r.uuid === selectedDocumentType);

    // selectedDocumentType is undefined, Therefore it's a document version modal, so don't warn user
    if (type === undefined) return false;

    const filterCriteria = dataGridMapFilterCriteria([]);

    if (customVariant) {
      filterCriteria.results = {
        customVariant: {
          operator: "in",
          value: [customVariant],
        },
        type: {
          operator: "eq",
          value: type?.name || "",
        },
      };
    } else if (supportsMultipleFiles) {
      filterCriteria.results = {
        customVariant: {
          operator: "in",
          value: [...(files?.map((f) => f.name) || [])],
        },
        type: {
          operator: "eq",
          value: type?.name || "",
        },
      };
    } else if (selectedDocumentVariant) {
      filterCriteria.results = {
        variant: {
          operator: "eq",
          value: selectedDocumentVariant,
        },
        type: {
          operator: "eq",
          value: type?.name || "",
        },
      };
    } else {
      filterCriteria.results = {
        type: {
          operator: "eq",
          value: type?.name || "",
        },
      };
    }

    return searchProjectDocuments({
      groupUuid: isGroup ? projectDetails?.group?.groupUuid || groupUuid || undefined : undefined,
      // Same fallback as the upload this guards, so a failed project read cannot leave the check unscoped.
      projectUuid: projectDetails?.uuid || projectUuid || undefined,
      paging: { beforeCursor: null, afterCursor: null, limit: 50 },
      filter: filterCriteria,
    }).then((response) => {
      if (
        (response.data?.results && isGroup && response.data?.results.filter((r) => r.groupId != null).length > 0) ||
        (response.data?.results && !isGroup && response.data?.results.filter((r) => r.groupId == null).length > 0)
      ) {
        return true;
      }
      return false;
    });
  };

  // Check for PublicExternalRegistry document
  const checkPublicExternalRegistry = async (projectDocumentUuid: string): Promise<boolean> => {
    return searchProjectDocumentHistory({
      projectDocumentUuid,
      paging: { beforeCursor: null, afterCursor: null, limit: 50 },
    }).then((response) => {
      if (
        response.data?.results.find(
          (r) => r.projectDocument.standardApproved && r.projectDocument.visibility === "Public"
        )
      ) {
        return true;
      }
      return false;
    });
  };

  /*
    Resolves the uuids of the documents an upload just created. project/document/upload returns no uuid, so
    the newest documents are re-read and matched back to the uploaded file names. Matching on the file name
    rather than on type/variant keeps a multi-file upload (one document per file) correct, and the
    newest-first sort keeps an earlier document of the same name from being picked up instead.

    Undefined when the read itself failed, which is not the same answer as none of the names matching: either
    leaves the caller unable to act on the upload, and the caller reports both rather than closing silently.
  */
  const resolveUploadedDocuments = async (uploadedFileNames: string[]): Promise<UploadedDocument[] | undefined> => {
    const response = await searchProjectDocuments({
      groupUuid: isGroup ? projectDetails?.group?.groupUuid || groupUuid || undefined : undefined,
      projectUuid: resolvedProjectUuid,
      paging: { beforeCursor: null, afterCursor: null, limit: 50 },
      sort: [{ key: "results.updatedAt", direction: "desc" }],
    });

    if (response.status !== Status.Success || !response.data?.results) return undefined;

    const wanted = new Set(uploadedFileNames);
    const seen = new Set<string>();

    return response.data.results.flatMap((document) => {
      const name = document.file?.filename;
      // One match per uploaded file: a name already matched belongs to an older version of that document.
      if (!name || !wanted.has(name) || seen.has(name)) return [];
      seen.add(name);
      return [{ uuid: document.uuid, fileName: name }];
    });
  };

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const variantUuid =
      documentVariants && documentVariants[0].options.find((v) => v.value === selectedDocumentVariant)?.key;

    if (
      visibility === undefined ||
      isGroup === undefined ||
      files === undefined ||
      (documentVariants &&
        (selectedDocumentVariant === undefined ||
          (selectedDocumentVariant === "Custom" && customVariant === undefined))) ||
      (uploadDocumentUuid === undefined && selectedDocumentType === undefined)
    ) {
      setErrors([
        {
          code: "MANDATORY_FIELDS",
          message: `Please complete the mandatory fields: Document type, Upload document${
            supportsMultipleFiles ? "'s" : ""
          }${documentVariants ? ", Variant type" : ""}${
            documentVariants && selectedDocumentVariant === "Custom" && customVariant === undefined
              ? ", Description"
              : ""
          }${visibility === undefined ? ", Public visibility" : ""}${isGroup ? ", Group document" : ""}`,
        },
      ]);
      return;
    }

    if (!showExistsWarning) {
      if (await checkExists()) {
        setShowExistsWarning(true);
        return;
      }
    }

    if (uploadDocumentUuid && !visibility && !showExternalRegistryWarning) {
      if (await checkPublicExternalRegistry(uploadDocumentUuid)) {
        setShowExternalRegistryWarning(true);
        return;
      }
    }

    let inputSelectedOrganisations = visibility || false ? selectedOrganisations || [] : [];
    if (visibility && currentOrganisationUuid && !inputSelectedOrganisations.includes(currentOrganisationUuid)) {
      inputSelectedOrganisations = [...inputSelectedOrganisations, currentOrganisationUuid];
    }

    // if latestDocumentDetails === undefined, then new document, else new version
    await uploadProjectDocument({
      files: files?.map((f) => ({ file: f })) || [],
      // eslint-disable-next-line no-nested-ternary
      visibility: visibility ? "Private" : "Public",
      groupUuid: isGroup ? projectDetails?.group?.groupUuid || groupUuid || null : null,
      projectUuid: !isGroup ? projectDetails?.uuid || projectUuid || null : null,
      documentTypeUuid: uploadDocumentUuid ? null : selectedDocumentType || null,
      targetOrganisationUuids: visibility ? inputSelectedOrganisations : null,
      // eslint-disable-next-line no-nested-ternary
      documentTypeVariantUuid: uploadDocumentUuid
        ? null
        : customVariant === undefined && supportsMultipleFiles === false
          ? variantUuid?.toString() || null
          : null,
      documentCustomVariant: uploadDocumentUuid ? null : customVariant || null,
      projectDocumentUuid: uploadDocumentUuid || null,
      externalReference: null,
      externalParty: null,
      standardApproved: null,
      isETLRequest: null,
    }).then(async (response) => {
      if (response.status === Status.Success) {
        Toast.success({ message: "Document uploaded successfully" });

        // Resolved before handleCancel, which clears the file selection this depends on.
        if (onUploaded) {
          const uploadedFileNames = files.map((file) => file.name);
          const uploadedDocuments = await resolveUploadedDocuments(uploadedFileNames);

          /*
            A document the lookup could not resolve cannot be reported to the caller, so nothing acts on it —
            the task overlay would link nothing and show no failure. The upload itself stands, so re-submitting
            is not the retry (it would upload a second document); what is reported is that the document exists
            but is not attached, named, so the user knows which one and does not assume the attach happened.
          */
          const unresolvedFileNames = uploadedFileNames.filter(
            (name) => !uploadedDocuments?.some((document) => document.fileName === name)
          );

          if (unresolvedFileNames.length > 0) {
            Toast.error({
              message: `${
                unresolvedFileNames.length > 1 ? "These documents were" : "The document was"
              } uploaded, but could not be attached: ${unresolvedFileNames.join(", ")}`,
            });
          }

          onUploaded(uploadedDocuments ?? []);
        }

        setRefreshDocuments(!refreshDocuments);
        handleCancel();
      }
      if (response.status === Status.Error && response.errors) {
        setErrors(
          response.errors.map((error) =>
            error.code === "VALIDATION_MIME_TYPE_NOT_SUPPORTED"
              ? { ...error, message: `Only ${supportedMimeTypes?.join(", ")} are supported for this document type` }
              : { ...error, message: getErrorMessageFromCode(error.code) }
          )
        );
      }
    });
  }, setIsHandleSubmitLoading);

  /*
    Only for the hosts that have no ProjectContext to read the project from. Skipped when context already has
    it, and when there is no uuid to read it by (a group-only upload passes groupUuid and no project).
  */
  useEffect(() => {
    if (projectDetailsInContext !== undefined || !resolvedProjectUuid) return undefined;

    // Tied to the project it was started for, so a response for one the modal has moved off is discarded.
    let isCurrentProject = true;

    getProjectDetails({ projectUuid: resolvedProjectUuid, projectType: null, projectExternalReference: null })
      .then((res) => {
        if (!isCurrentProject) return;

        if (res.status === Status.Success && res.data) {
          setFetchedProjectDetails(res.data);
          return;
        }

        Toast.error({ message: res.errors?.[0]?.message ?? "This project's document types could not be loaded" });
      })
      .catch(() => Toast.error({ message: "This project's document types could not be loaded" }))
      .finally(() => {
        if (isCurrentProject) setReadProjectUuid(resolvedProjectUuid);
      });

    return () => {
      isCurrentProject = false;
    };
  }, [projectDetailsInContext, resolvedProjectUuid]);

  /*
    Read once the project has settled: the type list is filtered by its standard, so searching it while the
    project is still outstanding would put a general-only list in the modal that the correct one may not win
    against.
  */
  useEffect(() => {
    if (isAwaitingProject) return;

    getDocumentDetails();
  }, [uploadDocumentLatestVersionUuid, closeModal, projectDetails, isAwaitingProject]);

  return {
    fileUploadRef,
    projectDetails,
    latestDocumentDetails,
    visibility,
    isGroup,
    maxFileSize,
    supportsMultipleFiles,
    relatedOrganisations,
    selectedDocumentVariant,
    documentTypes,
    documentVariants,
    isHandleSubmitLoading,
    handleVisibilityChange,
    setIsGroup,
    setFiles,
    setSelectedOrganisations,
    setSelectedDocumentVariant,
    setCustomVariant,
    handleCancel,
    handleDocumentTypeChange,
    handleSubmit,
    showExistsWarning,
    showExternalRegistryWarning,
    errors,
  };
};
